import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import isEmpty from 'lodash/isEmpty';
import split from 'lodash/split';
import Slider from 'react-slick';
import classNames from 'classnames';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faMagnifyingGlassPlus, faChevronLeft, faChevronRight } from '@fortawesome/free-solid-svg-icons';
import { FormattedMessage } from 'react-intl';

import AgeGatingContainer from 'Components/Product/AgeGatingContainer';
import ProductCover from 'Components/Utility/ProductCover';
import FluidImg from 'Components/Utility/FluidImg';
import CarouselArrow from 'Components/Carousel/CarouselArrow';
import CarouselPaging from 'Components/Carousel/CarouselPaging';
import LazyModal from 'Components/Utility/LazyModal';
import TrustPilotBanner from 'Components/Product/Carousel/TrustPilotBanner';

import 'Components/Product/Carousel/ProductPrintCarousel.scss';

function SliderButton({ className, onClick, type }) {
  const buttonLabel = type && type === 'left' ? 'Previous' : 'Next';
  return (
    <button
      aria-label={buttonLabel}
      className={className}
      onClick={() => { onClick(); }}
    >
      {(type && type === 'left') &&
        <FontAwesomeIcon icon={faChevronLeft} size="lg" className="arrow" />
      }
      {(type && type === 'right') &&
        <FontAwesomeIcon icon={faChevronRight} size="lg" className="arrow" />
      }
    </button>
  );
}

const ProductPrintCarousel = ({
  cover,
  coverAltText,
  name,
  displayType,
  coverType,
  sizesOverride,
  screenshots,
  passedAgeGate,
  ageRatings,
  dateOfBirth,
}) => {
  const [mainNav, setMainNav] = useState(null);
  const [thumbnailNav, setThumbnailNav] = useState(null);
  const [mainSlider, setMainSlider] = useState(null);
  const [thumbnailSlider, setThumbnailSlider] = useState(null);
  const [maxModalOpen, setMaxModalOpen] = useState(false);
  const [screenshotSlide, setScreenshotSlide] = useState(1);

  useEffect(() => {
    setMainNav(mainSlider);
    setThumbnailNav(thumbnailSlider);
  }, [mainSlider, thumbnailSlider]);

  const noMedia = isEmpty(screenshots);

  if (noMedia) {
    return (
      <div className="ProductPrintCarousel ProductPrintCarousel--singleCover">
        <ProductCover
          cover={cover}
          alt={coverAltText || `Product cover for ${name}`}
          sizesOverride={sizesOverride}
          displayType={displayType}
          coverType={coverType}
        />
        <TrustPilotBanner />
      </div>
    );
  }

  const ageGateThreshold = 18;

  let ageGateRequired = false;
  if (!passedAgeGate && ageRatings.book >= ageGateThreshold) {
    ageGateRequired = true;
  }

  if (dateOfBirth && dateOfBirth !== '') {
    const dobDate = new Date(dateOfBirth);
    const year = dobDate.getFullYear();
    const month = dobDate.getMonth() + 1;
    const day = dobDate.getDate();
    if (!year || !month || !day) return false;

    const today = Date.now();
    const userDate = new Date(year, month, day);
    const ageDifference = (today - userDate) / 1000 / 60 / 60 / 24 / 363.5;

    if (ageDifference >= (ageRatings.book)) {
      ageGateRequired = false;
    }
  }

  const slides = [];
  if (cover) {
    const alt = split(cover, '.')[0];
    slides.push({
      image: cover,
      alt,
      type: 'cover',
      number: slides.length + 1,
      order: '0',
      cover: true,
    });
  }
  if (!isEmpty(screenshots)) {
    screenshots.forEach((image, i) => {
      slides.push({
        image: image.slug,
        alt: image.alt,
        type: 'image',
        number: slides.length + 1,
        screenshotNumber: i + 1,
        order: image.order || '99',
      });
    });
  }

  const viewScreenshotModal = (screenshotNumber) => {
    setMaxModalOpen(true);
    setScreenshotSlide(screenshotNumber);
  };

  const changeScreenshot = (screenshotNumber, type) => {
    if (type === 'previous') {
      setScreenshotSlide(screenshotNumber - 1);
    }
    if (type === 'next') {
      setScreenshotSlide(screenshotNumber + 1);
    }
  };

  const totalScreenshots = slides.filter(slide => slide.screenshotNumber);
  const currentScreenshot = slides.find(slide => slide.screenshotNumber === screenshotSlide);
  const nextScreenshot = slides.find(slide => slide.screenshotNumber === screenshotSlide + 1);

  const settings = {
    asNavFor: '.slider-nav',
    focusOnSelect: true,
    centerMode: false,
    accessibility: true,
    centerPadding: '0px',
    infinite: true,
    slidesToShow: 1,
    slidesToScroll: 1,
    dots: false,
    arrows: false,
    prevArrow: <CarouselArrow ariaLabel="Previous" arrowDirection="left" containerClassName="ProductMediaCarousel__arrow ProductMediaCarousel__arrow--left" />,
    nextArrow: <CarouselArrow ariaLabel="Next" arrowDirection="right" containerClassName="ProductMediaCarousel__arrow ProductMediaCarousel__arrow--right" />,
    draggable: true,
    autoplay: false,
    autoplaySpeed: 7500,
    speed: 500,
    variableWidth: false,
    lazyLoad: 'ondemand',
    dotsClass: 'slick-dots slick-thumb',
    customPaging: i => (
      <CarouselPaging
        slideIndex={i}
        slideLength={Array.isArray(slides) ? slides.length : 0}
        slidesToShow={1}
      />
    ),
    responsive: [
      {
        breakpoint: 992,
        settings: {
          centerPadding: '0px',
          variableWidth: false,
          centerMode: false,
          slidesToScroll: 1,
          arrows: true,
        },
      },
    ],
  };

  const thumbnailSettings = {
    slidesToShow: 5,
    slidesToScroll: 1,
    asNavFor: '.slider-for',
    swipeToSlide: true,
    focusOnSelect: true,
    infinite: slides.length > 5,
    dots: false,
    arrows: true,
    nextArrow: <SliderButton type="right" />,
    prevArrow: <SliderButton type="left" />,
    lazyLoad: 'ondemand',
  };

  return (
    <React.Fragment>
      <div className="ProductPrintCarousel">
        <div className="ProductMediaCarousel__mainWrapper">
          <Slider
            {...settings}
            className="ProductPrintCarousel__main"
            asNavFor={thumbnailNav}
            ref={slider => setMainSlider(slider)}
          >
            {slides.map((slide) => {
              const key = slide.number + Math.random();

              if (slide.type === 'cover') {
                return (
                  <ProductCover
                    cover={slide.image}
                    alt={coverAltText || `Product cover for ${slide.alt}`}
                    sizesOverride={sizesOverride}
                    displayType={displayType}
                    coverType={coverType}
                    key={key}
                  />
                );
              }

              return (
                <div className={classNames('ProductPrintCarousel__main__slide', { 'ProductPrintCarousel__main__slide--ageGated': ageGateRequired })} key={key}>
                  <div className="responsive-image-island">
                    <div className="responsive-image responsive-image--16by9">
                      <FluidImg
                        className="img-force-full cover-image-foreground"
                        src={slide.image}
                        alt={slide.alt}
                        sizesOverride={sizesOverride}
                      />
                      <FluidImg
                        className="cover-image-background"
                        src={slide.image}
                        alt="Blurred background image"
                        sizesOverride={sizesOverride}
                      />
                    </div>
                  </div>

                  {ageGateRequired &&
                    <AgeGatingContainer ratings={ageRatings} lazyLoad={false} className="ProductPrintCarousel__main__slide__ageGating" />
                  }

                  {slide.type === 'image' && slide.screenshotNumber && !ageGateRequired &&
                    <button className="ProductPrintCarousel__main__maximiseButton" onClick={() => { viewScreenshotModal(slide.screenshotNumber); }}>
                      <FontAwesomeIcon icon={faMagnifyingGlassPlus} />
                    </button>
                  }
                </div>
              );
            })}
          </Slider>
        </div>

        <TrustPilotBanner />

        <Slider
          {...thumbnailSettings}
          asNavFor={mainNav}
          ref={slider => setThumbnailSlider(slider)}
          className={classNames('ProductPrintCarousel__track', { 'ProductPrintCarousel__track--ageGated': ageGateRequired }, { 'ProductPrintCarousel__track--single': slides.length <= 5 })}
        >
          {slides.map((slide) => {
            const key = slide.number + Math.random();

            if (slide.type === 'cover') {
              return (
                <ProductCover
                  adaptiveSize
                  cover={slide.image}
                  alt={coverAltText || `Product cover for ${slide.alt}`}
                  sizesOverride={sizesOverride}
                  displayType={displayType}
                  coverType={'square'}
                  key={key}
                />
              );
            }

            return (
              <button aria-label="View image" className="ProductPrintCarousel__track__slide" key={key}>
                <div className="responsive-image-island">
                  <div className="responsive-image responsive-image--1by1">
                    <FluidImg
                      src={slide.image}
                      alt={slide.alt}
                      className="cover-image-foreground"
                      sizesOverride="(min-width: 1520px) 208px, (min-width: 1200px) 176px, (min-width: 992px) 146px, 98vw"
                    />
                    <FluidImg
                      src={slide.image}
                      alt="Blurred background image"
                      className="cover-image-background"
                      sizesOverride="(min-width: 1520px) 208px, (min-width: 1200px) 176px, (min-width: 992px) 146px, 98vw"
                    />
                  </div>
                </div>
              </button>
            );
          })}
        </Slider>
      </div>

      {!isEmpty(totalScreenshots) &&
        <LazyModal
          open={maxModalOpen}
          setOpen={setMaxModalOpen}
          className="ProductMediaModal"
        >
          <>
            <div className="responsive-image-island">
              <div className="responsive-image responsive-image--16by9">
                <FluidImg
                  className="img-force-full top-image"
                  src={currentScreenshot.image}
                  alt={currentScreenshot.alt}
                  sizesOverride="(min-width: 1520px) 1340px, (min-width: 1200px) 1140px, (min-width: 992px) 960px, 90vw"
                />
                {nextScreenshot &&
                  <FluidImg
                    className="img-force-full bottom-image"
                    src={nextScreenshot.image}
                    alt={nextScreenshot.alt}
                    sizesOverride="(min-width: 1520px) 1340px, (min-width: 1200px) 1140px, (min-width: 992px) 960px, 90vw"
                  />
                }
              </div>
            </div>

            <div className="ProductMediaModal__controls">
              <div className="ProductMediaModal__controls__buttonContainer ProductMediaModal__controls__buttonContainer--previous">
                {currentScreenshot.screenshotNumber !== 1 &&
                  <button
                    className="ProductMediaModal__controls__buttonContainer__button"
                    onClick={() => { changeScreenshot(currentScreenshot.screenshotNumber, 'previous'); }}
                  >
                    <FormattedMessage
                      id="product-print-carousel.controls.button.previous"
                      defaultMessage="Previous"
                    />
                  </button>
                }
              </div>
              <div className="ProductMediaModal__controls__total">
                <FormattedMessage
                  id="product-print-carousel.controls.total"
                  defaultMessage="{current} of {total} screenshots"
                  values={{
                    current: currentScreenshot.screenshotNumber,
                    total: totalScreenshots.length,
                  }}
                />
              </div>
              <div className="ProductMediaModal__controls__buttonContainer ProductMediaModal__controls__buttonContainer--next">
                {currentScreenshot.screenshotNumber !== totalScreenshots.length &&
                  <button
                    className="ProductMediaModal__controls__buttonContainer__button"
                    onClick={() => { changeScreenshot(currentScreenshot.screenshotNumber, 'next'); }}
                  >
                    <FormattedMessage
                      id="product-print-carousel.controls.button.next"
                      defaultMessage="Next"
                    />
                  </button>
                }
              </div>
            </div>
          </>
        </LazyModal>
      }
    </React.Fragment>
  );
};

ProductPrintCarousel.propTypes = {
  cover: PropTypes.string,
  coverAltText: PropTypes.string,
  coverType: PropTypes.string,
  displayType: PropTypes.string,
  name: PropTypes.string,
  sizesOverride: PropTypes.string,
  screenshots: PropTypes.arrayOf(PropTypes.object),
  ageRatings: PropTypes.object,
  passedAgeGate: PropTypes.bool.isRequired,
  dateOfBirth: PropTypes.string,
};

ProductPrintCarousel.defaultProps = {
  cover: '',
  coverAltText: '',
  coverType: '',
  displayType: '',
  name: '',
  sizesOverride: '',
  screenshots: [],
  ageRatings: {},
  dateOfBirth: '',
};

const mapStateToProps = ({
  ageGating: { success },
  auth: { dob },
}) => ({
  passedAgeGate: success,
  dateOfBirth: dob,
});

export default connect(mapStateToProps)(ProductPrintCarousel);
